import React, { useState, useEffect, useRef } from 'react';
import { Bell, Clock, AlertCircle, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext.tsx';
import AllTasksModal from './AllTasksModal.tsx';

interface TaskReminder {
  type: string;
  task: {
    id: number;
    title: string;
    task_type: string;
    handoff_type?: string | null;
    priority: string;
    scheduled_for: string;
    created_by?: {
      id: number | string;
      type?: string;
    };
    assigned_to?: {
      id: number | string;
      type?: string;
    };
    contact: {
      id: number;
      name: string;
      phone: string;
    };
  };
  minutes_until: number;
  is_overdue: boolean;
  clinic_id?: number; // Adicionado para filtrar notificações por clínica
  user_id?: number | string;
  user_type?: string;
}

interface TaskNotificationsProps {
  websocketMessage?: any;
  isCollapsed?: boolean;
}

const normalizeUserType = (type?: string | null) => {
  if (!type) return '';
  return type === 'client' ? 'master' : type;
};

const getLocalStorageItem = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setLocalStorageItem = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Keep in-memory notifications independent from persistence.
  }
};

const removeLocalStorageItem = (key: string) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
};

const getCurrentNotificationScope = () => {
  const userType = normalizeUserType(getLocalStorageItem('user_type'));
  const userId = getLocalStorageItem('user_id') || getLocalStorageItem('client_id') || '';
  const clinicId = getLocalStorageItem('clinic_id') || '';

  return { clinicId, userId, userType };
};

const getNotificationStorageKeys = () => {
  const { clinicId, userId, userType } = getCurrentNotificationScope();
  const prefix = `taskNotifications:${clinicId || 'sem-clinica'}:${userType || 'sem-tipo'}:${userId || 'sem-usuario'}`;

  return {
    notifications: `${prefix}:items`,
    unreadCount: `${prefix}:unreadCount`,
  };
};

const removeLegacyNotificationStorage = () => {
  removeLocalStorageItem('taskNotifications');
  removeLocalStorageItem('taskUnreadCount');
};

const isSameId = (left?: number | string | null, right?: number | string | null) => {
  return left !== undefined && left !== null && right !== undefined && right !== null && String(left) === String(right);
};

const getTaskOwner = (task: any) => {
  return task?.assigned_to || task?.created_by || null;
};

const isTaskVisibleToCurrentUser = (task: any) => {
  const { userId, userType } = getCurrentNotificationScope();
  const owner = getTaskOwner(task);

  if (!userId || !owner?.id || !isSameId(owner.id, userId)) {
    return false;
  }

  const ownerType = normalizeUserType(owner.type);
  return !ownerType || !userType || ownerType === userType;
};

const isNotificationVisibleToCurrentUser = (notification: TaskReminder | any) => {
  const { clinicId, userId, userType } = getCurrentNotificationScope();

  if (!clinicId || !userId) {
    return false;
  }

  if (!notification?.clinic_id || String(notification.clinic_id) !== clinicId) {
    return false;
  }

  if (notification.user_id !== undefined && notification.user_id !== null) {
    if (!isSameId(notification.user_id, userId)) {
      return false;
    }

    const notificationUserType = normalizeUserType(notification.user_type);
    return !notificationUserType || !userType || notificationUserType === userType;
  }

  return isTaskVisibleToCurrentUser(notification.task);
};

const TaskNotifications: React.FC<TaskNotificationsProps> = ({ websocketMessage, isCollapsed = false }) => {
  const { isDark } = useTheme();
  const [notifications, setNotifications] = useState<TaskReminder[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAllTasksModal, setShowAllTasksModal] = useState(false);
  const [lastProcessedMessage, setLastProcessedMessage] = useState<string | null>(null);
  const announcedHandoffIds = useRef(new Set<string>());
  const navigate = useNavigate();

  // Carregar notificações do localStorage ao montar o componente
  useEffect(() => {
    const storageKeys = getNotificationStorageKeys();
    const savedNotifications = getLocalStorageItem(storageKeys.notifications);
    const savedUnreadCount = getLocalStorageItem(storageKeys.unreadCount);

    removeLegacyNotificationStorage();
    
    if (savedNotifications) {
      try {
        const parsedNotifications = JSON.parse(savedNotifications);
        const visibleNotifications = Array.isArray(parsedNotifications)
          ? parsedNotifications.filter(isNotificationVisibleToCurrentUser)
          : [];
        visibleNotifications.forEach((notification: TaskReminder) => {
          if (notification.type === 'human_handoff') {
            announcedHandoffIds.current.add(String(notification.task.id));
          }
        });
        setNotifications(visibleNotifications);
      } catch (e) {
        console.error('Erro ao carregar notificações salvas:', e);
      }
    }
    
    if (savedUnreadCount) {
      const parsedUnreadCount = parseInt(savedUnreadCount, 10);
      setUnreadCount(Number.isNaN(parsedUnreadCount) ? 0 : parsedUnreadCount);
    }
  }, []);

  // Salvar notificações no localStorage quando mudarem
  useEffect(() => {
    const storageKeys = getNotificationStorageKeys();
    setLocalStorageItem(storageKeys.notifications, JSON.stringify(notifications));
    setLocalStorageItem(storageKeys.unreadCount, unreadCount.toString());
  }, [notifications, unreadCount]);

  useEffect(() => {
    if (!websocketMessage) return;

    if (websocketMessage.type === 'task_completed') {
      if (!isNotificationVisibleToCurrentUser(websocketMessage)) return;
      setNotifications(prev => {
        const hasNotification = prev.some(n => isSameId(n.task.id, websocketMessage.task_id));
        if (hasNotification) {
          setUnreadCount(current => Math.max(0, current - 1));
        }
        return prev.filter(n => !isSameId(n.task.id, websocketMessage.task_id));
      });
      return;
    }

    const isLiveHandoff = (
      websocketMessage.type === 'human_handoff' ||
      (websocketMessage.type === 'task_reminder' && websocketMessage.legacy_human_handoff === true)
    ) && websocketMessage.task;
    const pendingHandoffTasks = Array.isArray(websocketMessage.human_handoff_tasks)
      ? websocketMessage.human_handoff_tasks
      : [];
    const hasPendingHandoffSnapshot = Array.isArray(websocketMessage.human_handoff_tasks);
    const pendingHandoffSnapshotIsVisible = hasPendingHandoffSnapshot &&
      isNotificationVisibleToCurrentUser(websocketMessage);
    const isLegacyNotification = websocketMessage.type === 'task_reminder' || websocketMessage.type === 'overdue_tasks';
    if (!isLiveHandoff && !hasPendingHandoffSnapshot && !isLegacyNotification) return;

    const taskIds = [
      ...(websocketMessage.tasks || []).map((task: any) => task.id),
      ...pendingHandoffTasks.map((task: any) => task.id),
      websocketMessage.task?.id,
    ].filter(Boolean);
    const messageId = [
      websocketMessage.type,
      websocketMessage.clinic_id,
      websocketMessage.user_type,
      websocketMessage.user_id,
      websocketMessage.count,
      JSON.stringify(taskIds),
      websocketMessage.timestamp || websocketMessage.task?.scheduled_for || '',
    ].join('_');

    if (lastProcessedMessage === messageId) {
      console.log('[TaskNotifications] Mensagem já processada, ignorando duplicação');
      return;
    }

    console.log('[TaskNotifications] Processando mensagem de tarefa:', websocketMessage.type);
    setLastProcessedMessage(messageId);

    if (isLiveHandoff) {
      const handoffNotification = { ...websocketMessage, type: 'human_handoff' } as TaskReminder;
      if (!isNotificationVisibleToCurrentUser(handoffNotification)) return;

      const handoffId = String(handoffNotification.task.id);
      const shouldAnnounce = !announcedHandoffIds.current.has(handoffId);
      announcedHandoffIds.current.add(handoffId);
      setNotifications(prev => {
        const exists = prev.some(n => isSameId(n.task.id, handoffNotification.task.id));
        if (exists) {
          return prev.map(n => isSameId(n.task.id, handoffNotification.task.id) ? handoffNotification : n);
        }
        setUnreadCount(current => current + 1);
        return [handoffNotification, ...prev].slice(0, 10);
      });
      if (shouldAnnounce) {
        playNotificationSound();
        showBrowserNotification(handoffNotification);
      }
      return;
    }

    let scopedHandoffTasks = pendingHandoffTasks;
    if ((websocketMessage.user_id || websocketMessage.clinic_id) && scopedHandoffTasks.length > 0) {
      const scopeIsVisible = isNotificationVisibleToCurrentUser({
        ...websocketMessage,
        task: scopedHandoffTasks[0],
      });
      scopedHandoffTasks = scopeIsVisible
        ? scopedHandoffTasks
        : [];
    } else if (!websocketMessage.user_id) {
      scopedHandoffTasks = scopedHandoffTasks.filter(isTaskVisibleToCurrentUser);
    }

    if (pendingHandoffSnapshotIsVisible) {
      const currentHandoffIds = new Set(scopedHandoffTasks.map((task: any) => String(task.id)));
      setNotifications(prev => {
        const removedHandoffCount = prev.filter(notification =>
          notification.type === 'human_handoff' && !currentHandoffIds.has(String(notification.task.id))
        ).length;
        if (removedHandoffCount > 0) {
          setUnreadCount(current => Math.max(0, current - removedHandoffCount));
        }
        return prev.filter(notification =>
          notification.type !== 'human_handoff' || currentHandoffIds.has(String(notification.task.id))
        );
      });
    }

    const handoffNotifications: TaskReminder[] = scopedHandoffTasks.map((task: any) => {
      const scheduledTime = new Date(task.scheduled_for).getTime();
      const minutesUntil = Number.isFinite(scheduledTime)
        ? Math.ceil((scheduledTime - Date.now()) / 60000)
        : 0;
      const owner = getTaskOwner(task);
      return {
        type: 'human_handoff',
        task: {
          id: task.id,
          title: task.title,
          task_type: task.task_type || 'message',
          priority: task.priority || 'urgent',
          scheduled_for: task.scheduled_for,
          created_by: task.created_by,
          assigned_to: task.assigned_to,
          contact: {
            id: task.contact_id || task.contact?.id || 0,
            name: task.contact_name || task.contact?.name || '',
            phone: task.contact_phone || task.contact?.phone || '',
          },
        },
        minutes_until: minutesUntil,
        is_overdue: minutesUntil <= 0,
        clinic_id: websocketMessage.clinic_id,
        user_id: websocketMessage.user_id || owner?.id,
        user_type: normalizeUserType(websocketMessage.user_type || owner?.type) || undefined,
      };
    });

    if (handoffNotifications.length > 0) {
      setNotifications(prev => {
        const handoffIds = new Set(handoffNotifications.map(n => String(n.task.id)));
        const existingIds = new Set(prev.map(n => String(n.task.id)));
        const newCount = handoffNotifications.filter(n => !existingIds.has(String(n.task.id))).length;
        if (newCount > 0) setUnreadCount(current => current + newCount);
        return [
          ...handoffNotifications,
          ...prev.filter(n => !handoffIds.has(String(n.task.id))),
        ].slice(0, 10);
      });
      handoffNotifications.forEach(notification => {
        const handoffId = String(notification.task.id);
        if (!announcedHandoffIds.current.has(handoffId)) {
          announcedHandoffIds.current.add(handoffId);
          showBrowserNotification(notification);
        }
      });
    }

    if (websocketMessage.type === 'task_reminder' && websocketMessage.task) {
      const newNotification = websocketMessage as TaskReminder;
      if (!isNotificationVisibleToCurrentUser(newNotification)) return;
      setNotifications(prev => {
        const exists = prev.some(n => isSameId(n.task.id, newNotification.task.id));
        if (exists) return prev.map(n => isSameId(n.task.id, newNotification.task.id) ? newNotification : n);
        setUnreadCount(current => current + 1);
        return [newNotification, ...prev].slice(0, 10);
      });
      playNotificationSound();
      showBrowserNotification(newNotification);
      return;
    }

    if (websocketMessage.type === 'overdue_tasks') {
      const { tasks } = websocketMessage;
      const handoffIds = new Set([
        ...pendingHandoffTasks.map((task: any) => String(task.id)),
        ...announcedHandoffIds.current,
      ]);
      const isHumanHandoffTask = (task: any) =>
        Boolean(task.handoff_type) ||
        (task.task_type === 'message' && task.title === 'Atendimento humano solicitado');
      const visibleTasks = (tasks || []).filter((task: any) =>
        !handoffIds.has(String(task.id)) && !isHumanHandoffTask(task) &&
        (websocketMessage.user_id || isTaskVisibleToCurrentUser(task))
      );

      if ((websocketMessage.user_id || websocketMessage.clinic_id) && visibleTasks.length > 0 &&
          !isNotificationVisibleToCurrentUser({ ...websocketMessage, task: visibleTasks[0] })) {
        return;
      }
      const overdueCount = visibleTasks.length;
      if (overdueCount === 0) return;

      const newNotifications: TaskReminder[] = visibleTasks.map((task: any) => {
        const scheduledTime = new Date(task.scheduled_for);
        const minutesOverdue = Math.floor((Date.now() - scheduledTime.getTime()) / 60000);
        const owner = getTaskOwner(task);
        return {
          type: 'overdue_task',
          task: {
            id: task.id,
            title: task.title,
            task_type: task.task_type || 'custom',
            handoff_type: task.handoff_type || null,
            priority: task.priority,
            scheduled_for: task.scheduled_for,
            created_by: task.created_by,
            assigned_to: task.assigned_to,
            contact: {
              id: task.contact_id || task.contact?.id || 0,
              name: task.contact_name || task.contact?.name || '',
              phone: task.contact_phone || task.contact?.phone || '',
            },
          },
          minutes_until: -minutesOverdue,
          is_overdue: true,
          clinic_id: websocketMessage.clinic_id,
          user_id: websocketMessage.user_id || owner?.id,
          user_type: normalizeUserType(websocketMessage.user_type || owner?.type) || undefined,
        };
      });

      setNotifications(prev => {
        const filtered = prev.filter(n => !n.is_overdue || n.type === 'human_handoff');
        const existingOverdueIds = new Set(prev.filter(n => n.is_overdue).map(n => String(n.task.id)));
        const reallyNewCount = newNotifications.filter(n => !existingOverdueIds.has(String(n.task.id))).length;
        if (reallyNewCount > 0) setUnreadCount(current => current + reallyNewCount);
        return [...newNotifications, ...filtered].slice(0, 10);
      });

      playNotificationSound();
      showBrowserNotificationForOverdue(overdueCount);
    }
  }, [websocketMessage]);

  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.play().catch(() => {
        // Ignore audio play errors
      });
    } catch (e) {
      // Ignore if audio file not found
    }
  };

  const showBrowserNotification = (reminder: TaskReminder) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const isHumanHandoff = reminder.type === 'human_handoff';
      const notification = new Notification(isHumanHandoff ? 'Atendimento humano' : 'Lembrete de Tarefa', {
        body: `${reminder.task.title} - ${reminder.task.contact.name}`,
        icon: '/fav-mobile-sminy.jpg',
        tag: `task-${reminder.task.id}`,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        // Navegar para o chat com o contato selecionado via state
        navigate('/chat', { 
          state: { 
            selectedPhone: reminder.task.contact.phone,
            selectedContact: reminder.task.contact
          } 
        });
        // Fechar o painel de notificações
        setShowNotifications(false);
        // Remover a notificação da lista
        setNotifications(prev => prev.filter(n => n.task.id !== reminder.task.id));
      };

      setTimeout(() => notification.close(), 5000);
    }
  };

  const showBrowserNotificationForOverdue = (count: number) => {
    if (count <= 0) return;
    const message = count === 1
      ? 'Você tem 1 tarefa em atraso'
      : `Você tem ${count} tarefas em atraso`;
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('Tarefas em Atraso', {
        body: message,
        icon: '/fav-mobile-sminy.jpg',
        tag: 'overdue-tasks',
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        // Abrir modal de todas as tarefas
        setShowAllTasksModal(true);
        setShowNotifications(false);
      };

      setTimeout(() => notification.close(), 5000);
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  };


  const getTaskTypeIcon = (type: string) => {
    switch (type) {
      case 'message':
        return '💬';
      case 'call':
        return '📞';
      case 'email':
        return '📧';
      default:
        return '📋';
    }
  };

  const formatTimeUntil = (minutes: number, isOverdue: boolean) => {
    const absMinutes = Math.abs(minutes);
    
    if (isOverdue) {
      if (absMinutes < 60) {
        return `Atrasada há ${absMinutes} minutos`;
      } else if (absMinutes < 1440) { // Less than 24 hours
        const hours = Math.floor(absMinutes / 60);
        const remainingMinutes = absMinutes % 60;
        if (remainingMinutes === 0) {
          return `Atrasada há ${hours} hora${hours > 1 ? 's' : ''}`;
        } else {
          return `Atrasada há ${hours}h ${remainingMinutes}min`;
        }
      } else { // More than 24 hours
        const days = Math.floor(absMinutes / 1440);
        const remainingHours = Math.floor((absMinutes % 1440) / 60);
        if (remainingHours === 0) {
          return `Atrasada há ${days} dia${days > 1 ? 's' : ''}`;
        } else {
          return `Atrasada há ${days}d ${remainingHours}h`;
        }
      }
    } else if (minutes <= 0) {
      return 'Agora';
    } else if (minutes < 60) {
      return `Em ${minutes} minutos`;
    } else {
      const hours = Math.floor(minutes / 60);
      return `Em ${hours} hora${hours > 1 ? 's' : ''}`;
    }
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
    setShowNotifications(false);

    const storageKeys = getNotificationStorageKeys();
    removeLocalStorageItem(storageKeys.notifications);
    removeLocalStorageItem(storageKeys.unreadCount);
    removeLegacyNotificationStorage();
  };

  useEffect(() => {
    // Request notification permission on mount
    requestNotificationPermission();
  }, []);

  return (
    <div className={`border-t ${
      isDark ? 'border-gray-800' : 'border-gray-200'
    }`}>
      {/* Notification Header - Always visible */}
      <button
        aria-label="Notificações"
        aria-expanded={isCollapsed ? showAllTasksModal : showNotifications}
        aria-haspopup={isCollapsed ? 'dialog' : undefined}
        onClick={() => {
          if (isCollapsed) {
            setShowAllTasksModal(true);
            setShowNotifications(false);
            setUnreadCount(0);
            return;
          }

          setShowNotifications(!showNotifications);
          if (!showNotifications) {
            setUnreadCount(0);
          }
        }}
        className={`
          w-full flex items-center gap-2 transition-all duration-150
          ${isCollapsed ? 'justify-center px-2 py-2' : 'justify-between px-3 py-2.5'}
          ${
            isDark
              ? 'hover:bg-gray-800'
              : 'hover:bg-gray-50'
          }
        `}
      >
        <div className={`flex items-center ${isCollapsed ? '' : 'gap-2.5'}`}>
          <div className="relative">
            <Bell className={`w-[18px] h-[18px] transition-all duration-150 ${
              unreadCount > 0
                ? 'text-sminy-primary'
                : isDark
                  ? 'text-gray-400'
                  : 'text-gray-500'
            }`} />
            {unreadCount > 0 && (
              <span className={`absolute -top-1 -right-1 bg-red-500 text-white text-[9px] rounded-full min-w-[14px] h-[14px] px-1 flex items-center justify-center font-semibold ring-2 ${
                isDark ? 'ring-[#0f172a]' : 'ring-white'
              }`}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          {!isCollapsed && <span className={`text-[12.5px] font-medium ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>Notificações</span>}
        </div>

        {!isCollapsed && (
          <ChevronDown className={`
            w-4 h-4 transition-transform duration-200
            ${showNotifications ? 'rotate-180' : ''}
            ${
              isDark ? 'text-gray-500' : 'text-gray-400'
            }
          `} />
        )}
      </button>

      {/* Expandable Notifications Panel */}
      <div className={`
        overflow-hidden transition-all duration-300 ease-in-out
        ${showNotifications ? 'max-h-[400px]' : 'max-h-0'}
      `}>
        <div className={`border-t ${
          isDark ? 'border-gray-800' : 'border-gray-200'
        }`}>
          {/* Quick Actions */}
          {notifications.length > 0 && !isCollapsed && (
            <div className={`px-3 py-2 flex justify-between items-center ${
              isDark ? 'bg-gray-900/40' : 'bg-gray-50'
            }`}>
              <span className={`text-[10.5px] font-semibold uppercase tracking-[0.08em] ${
                isDark ? 'text-gray-500' : 'text-gray-500'
              }`}>
                {notifications.length} {notifications.length === 1 ? 'lembrete' : 'lembretes'}
              </span>
              <button
                onClick={clearNotifications}
                className={`text-[11px] font-medium transition-colors ${
                  isDark
                    ? 'text-gray-400 hover:text-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Limpar tudo
              </button>
            </div>
          )}

          {/* Notifications List - Altura reduzida para deixar espaço para o botão */}
          <div className="overflow-y-auto max-h-[280px]">
            {notifications.length === 0 ? (
              <div className={`${isCollapsed ? 'p-3' : 'px-4 py-6'} text-center`}>
                <div className={`${isCollapsed ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg flex items-center justify-center mx-auto mb-2 ${
                  isDark ? 'bg-gray-800' : 'bg-gray-100'
                }`}>
                  <Bell className={`${isCollapsed ? 'h-4 w-4' : 'h-5 w-5'} ${
                    isDark ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                </div>
                {!isCollapsed && (
                  <>
                    <p className={`text-[12.5px] font-medium ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>Nenhuma notificação</p>
                    <p className={`text-[11px] mt-0.5 ${
                      isDark ? 'text-gray-500' : 'text-gray-500'
                    }`}>Os lembretes aparecerão aqui</p>
                  </>
                )}
              </div>
            ) : (
              <div className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                {notifications.map((notification, index) => (
                  <div
                    key={`${notification.task.id}-${index}`}
                    className={`
                      ${isCollapsed ? 'p-2' : 'px-3 py-2.5'}
                      transition-all duration-150 cursor-pointer
                      ${notification.type === 'human_handoff'
                        ? isDark
                          ? 'border-l-2 border-sminy-primary bg-sminy-primary/10 hover:bg-sminy-primary/15'
                          : 'border-l-2 border-sminy-primary bg-sminy-primary/5 hover:bg-sminy-primary/10'
                        : notification.is_overdue
                        ? isDark
                          ? 'bg-red-500/5 hover:bg-red-500/10'
                          : 'bg-red-50/50 hover:bg-red-50'
                        : isDark
                          ? 'hover:bg-gray-800/60'
                          : 'hover:bg-gray-50'
                      }
                    `}
                    onClick={() => {
                      navigate('/chat', {
                        state: {
                          selectedPhone: notification.task.contact.phone,
                          selectedContact: notification.task.contact
                        }
                      });
                      setShowNotifications(false);
                      setNotifications(prev => prev.filter(n => n.task.id !== notification.task.id));
                    }}
                  >
                    {isCollapsed ? (
                      <div className="flex justify-center">
                        <div className={`
                          w-7 h-7 rounded-md flex items-center justify-center text-xs
                          ${notification.type === 'human_handoff'
                            ? isDark ? 'bg-sminy-primary/15 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'
                            : notification.is_overdue
                            ? isDark ? 'bg-red-500/15 text-red-400' : 'bg-red-100 text-red-600'
                            : notification.task.priority === 'urgent'
                              ? isDark ? 'bg-orange-500/15 text-orange-400' : 'bg-orange-100 text-orange-600'
                              : isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
                          }
                        `}>
                          {getTaskTypeIcon(notification.task.task_type)}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2.5">
                        <div className={`
                          w-8 h-8 rounded-lg flex items-center justify-center text-sm shrink-0
                          ${notification.type === 'human_handoff'
                            ? isDark ? 'bg-sminy-primary/15 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'
                            : notification.is_overdue
                            ? isDark ? 'bg-red-500/15 text-red-400' : 'bg-red-100 text-red-600'
                            : notification.task.priority === 'urgent'
                              ? isDark ? 'bg-orange-500/15 text-orange-400' : 'bg-orange-100 text-orange-600'
                              : notification.task.priority === 'high'
                                ? isDark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-100 text-amber-700'
                                : isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
                          }
                        `}>
                          {getTaskTypeIcon(notification.task.task_type)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <h4 className={`font-medium text-[12.5px] truncate ${
                            isDark ? 'text-gray-200' : 'text-gray-800'
                          }`}>
                            {notification.task.title}
                          </h4>

                          {notification.type === 'human_handoff' && (
                            <span className="inline-flex mt-1 rounded-full bg-sminy-primary/10 px-2 py-0.5 text-[10px] font-semibold text-sminy-primary">
                              Suporte humano
                            </span>
                          )}

                          <p className={`text-[11.5px] mt-0.5 truncate ${
                            isDark ? 'text-gray-500' : 'text-gray-600'
                          }`}>
                            {notification.task.contact.name}
                          </p>

                          <div className="flex items-center gap-1.5 mt-1.5 text-[11px]">
                            <span className={`
                              flex items-center gap-1
                              ${notification.is_overdue
                                ? isDark ? 'text-red-400 font-medium' : 'text-red-600 font-medium'
                                : isDark
                                  ? 'text-gray-500'
                                  : 'text-gray-500'
                              }
                            `}>
                              {notification.is_overdue ? (
                                <AlertCircle className="h-3 w-3" />
                              ) : (
                                <Clock className="h-3 w-3" />
                              )}
                              {formatTimeUntil(notification.minutes_until, notification.is_overdue)}
                            </span>
                          </div>
                        </div>

                        <span className={`
                          text-[10px] font-semibold px-1.5 py-0.5 rounded-md shrink-0
                          ${notification.task.priority === 'urgent'
                            ? isDark ? 'bg-red-500/15 text-red-400' : 'bg-red-100 text-red-700'
                            : notification.task.priority === 'high'
                              ? isDark ? 'bg-orange-500/15 text-orange-400' : 'bg-orange-100 text-orange-700'
                              : notification.task.priority === 'medium'
                                ? isDark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-100 text-amber-700'
                                : isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
                          }
                        `}>
                          {notification.task.priority === 'urgent' ? 'U' :
                           notification.task.priority === 'high' ? 'A' :
                           notification.task.priority === 'medium' ? 'M' : 'B'}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* View All Tasks */}
          {!isCollapsed && (
            <div className={`px-3 py-2 border-t ${
              isDark
                ? 'border-gray-800 bg-gray-900/40'
                : 'border-gray-200 bg-gray-50'
            }`}>
              <button
                className={`w-full text-[11.5px] font-semibold py-1.5 px-2 rounded-md transition-colors ${
                  isDark
                    ? 'text-sminy-primary hover:bg-sminy-primary/10'
                    : 'text-sminy-primary hover:bg-sminy-primary/10'
                }`}
                onClick={() => {
                  setShowAllTasksModal(true);
                  setShowNotifications(false);
                }}
              >
                Ver todas as tarefas →
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal de todas as tarefas */}
      <AllTasksModal 
        isOpen={showAllTasksModal}
        onClose={() => setShowAllTasksModal(false)}
        onTaskClick={(task) => {
          // Navegar para o chat do contato da tarefa
          navigate('/chat', { 
            state: { 
              selectedPhone: task.contact_phone,
              selectedContact: {
                id: task.contact_id,
                name: task.contact_name,
                phone: task.contact_phone
              }
            } 
          });
          setShowAllTasksModal(false);
        }}
      />
    </div>
  );
};

export default TaskNotifications;
